import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth, anonymize } from "../App";
import { 
  Search, Plus, Filter, Eye, Trash2, Building2, Calendar,
  User, ChevronLeft, ChevronRight, CalendarX, CalendarCheck, Check, X as XIcon, FileText,
  ChevronsLeft, ChevronsRight, ScanLine
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import NewCaseDialog from "../components/NewCaseDialog";
import QRScannerDialog from "../components/QRScannerDialog";
import { BranchFilterSelect } from "../components/Workspace";
import { readWorkspace } from "../utils/workspace";
import { DateInput } from "../components/ui/date-input";

const ITEMS_PER_PAGE = 50;
const FLAG_FILTERS = [
  { key: "has_order", label: "Διάταξη", testid: "order" },
  { key: "has_withdrawal", label: "Παραίτηση", testid: "withdrawal" },
  { key: "mediation_referred", label: "Υπαγωγή σε Διαμεσολάβηση", testid: "mediation-referred" },
  { key: "mediation_success", label: "Πρακτικό Επιτ. Διαμεσολάβησης", testid: "mediation-success" },
  { key: "pretrial", label: "Υπάγεται σε Προδικαστικό", testid: "pretrial" },
  { key: "has_settlement_214a", label: "Εξωδική Επίλυση 214Α ΚΠολΔ", testid: "settlement-214a" },
];
const EMPTY_FLAGS = { has_order: "all", has_withdrawal: "all", mediation_referred: "all", mediation_success: "all", pretrial: "all", has_settlement_214a: "all" };

const CasesList = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const privacy = user?.privacy_mode || false;
  const p = (text) => privacy ? anonymize(text) : text;
  const [cases, setCases] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [courtFilter, setCourtFilter] = useState(() => {
    // Non-admin: default to their single court type if they have only one· αλλιώς ο Χώρος Εργασίας (iter109)
    const ct = user?.court_types || ["Μονομελές", "Πολυμελές"];
    return ct.length === 1 ? ct[0] : (readWorkspace(user)?.court_type || "");
  });
  const [branchFilter, setBranchFilter] = useState(() => readWorkspace(user)?.branch || "");
  const [statusFilter, setStatusFilter] = useState("");
  const [subjectFilter, setSubjectFilter] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [caseTypeFilter, setCaseTypeFilter] = useState("");
  const [caseTypes, setCaseTypes] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteId, setDeleteId] = useState(null);
  const [showNewCase, setShowNewCase] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [showBulkSubject, setShowBulkSubject] = useState(false);
  const [bulkSubjectValue, setBulkSubjectValue] = useState("");
  const [hearingFilter, setHearingFilter] = useState("");
  const [filingDateFrom, setFilingDateFrom] = useState("");
  const [filingDateTo, setFilingDateTo] = useState("");
  const [judgeFilter, setJudgeFilter] = useState("");
  const [pinakioFilter, setPinakioFilter] = useState("");
  const [flagFilters, setFlagFilters] = useState(EMPTY_FLAGS);
  const [editingHearing, setEditingHearing] = useState(null);
  const [hearingDateValue, setHearingDateValue] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [showQrScanner, setShowQrScanner] = useState(false);
  const navigate = useNavigate();

  // Όταν ολοκληρωθεί η σάρωση QR: αν είναι URL, πλοηγούμαστε στον φάκελο,
  // αλλιώς γεμίζουμε την μπάρα αναζήτησης με το αποκωδικοποιημένο κείμενο.
  const handleQrScanned = useCallback((decodedText) => {
    setShowQrScanner(false);
    if (!decodedText) return;
    try {
      // Match /cases/<id> από URL ή σκέτο id
      const m = decodedText.match(/\/cases\/([^?#/]+)/i);
      if (m && m[1]) {
        navigate(`/cases/${m[1]}`);
        return;
      }
      // Αν είναι plain url, άνοιξέ το ως αναζήτηση
      setSearch(decodedText);
      toast.success("QR αποκωδικοποιήθηκε: " + decodedText);
    } catch {
      setSearch(decodedText);
    }
  }, [navigate]);

  useEffect(() => { fetchSubjects(); fetchCaseTypes(); }, []);

  // Debounced search - wait 400ms before fetching
  useEffect(() => {
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => {
      setCurrentPage(1);
      fetchCases(1);
    }, 400);
    setSearchTimeout(timeout);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Immediate fetch on filter changes (not search)
  useEffect(() => {
    setCurrentPage(1);
    fetchCases(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courtFilter, branchFilter, statusFilter, subjectFilter, caseTypeFilter, hearingFilter, filingDateFrom, filingDateTo]);

  // Fetch on page change
  useEffect(() => {
    fetchCases(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const fetchSubjects = async () => {
    try { const r = await axios.get(`${API}/cases/subjects`); setSubjects(r.data); }
    catch { /* ignore */ }
  };

  const fetchCaseTypes = async () => {
    try {
      const r = await axios.get(`${API}/cases/case-types-with-counts`);
      setCaseTypes(r.data?.items || []);
    } catch { /* ignore */ }
  };

  const fetchCases = useCallback(async (page) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("page", page || 1);
      params.append("limit", ITEMS_PER_PAGE);
      if (search) params.append("search", search);
      if (courtFilter && courtFilter !== "all") params.append("court_type", courtFilter);
      if (branchFilter) params.append("branch", branchFilter);
      if (statusFilter && statusFilter !== "all") params.append("status", statusFilter);
      if (subjectFilter && subjectFilter !== "all") params.append("subject", subjectFilter);
      if (caseTypeFilter && caseTypeFilter !== "all") params.append("case_type", caseTypeFilter);
      if (hearingFilter && hearingFilter !== "all") params.append("hearing_status", hearingFilter);
      if (filingDateFrom) params.append("filing_date_from", filingDateFrom);
      if (filingDateTo) params.append("filing_date_to", filingDateTo);
      if (judgeFilter) params.append("judge_name", judgeFilter);
      if (pinakioFilter) params.append("pinakio", pinakioFilter);
      Object.entries(flagFilters).forEach(([k, v]) => { if (v && v !== "all") params.append(k, v); });
      const r = await axios.get(`${API}/cases?${params.toString()}`);
      setCases(r.data.cases || []);
      setTotalCount(r.data.total_count || 0);
      setTotalPages(r.data.total_pages || 0);
      setSelectedIds([]);
    } catch { toast.error("Σφάλμα φόρτωσης υποθέσεων"); }
    finally { setLoading(false); }
  }, [search, courtFilter, branchFilter, statusFilter, subjectFilter, caseTypeFilter, hearingFilter, filingDateFrom, filingDateTo, judgeFilter, pinakioFilter, flagFilters]);

  // Debounced trigger για judge/pinakio φίλτρα
  useEffect(() => {
    const t = setTimeout(() => { setCurrentPage(1); fetchCases(1); }, 400);
    return () => clearTimeout(t);
  }, [judgeFilter, pinakioFilter, fetchCases]);

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/cases/${deleteId}`);
      toast.success("Η υπόθεση διαγράφηκε");
      fetchCases(currentPage);
    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα διαγραφής"); }
    finally { setDeleteId(null); }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    try {
      await axios.post(`${API}/cases/bulk-delete`, { case_ids: selectedIds });
      toast.success(`Διαγράφηκαν ${selectedIds.length} υποθέσεις`);
      setSelectedIds([]);
      setShowBulkDelete(false);
      fetchCases(currentPage);
    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα μαζικής διαγραφής"); }
  };

  const handleSaveHearingDate = async (caseId) => {
    if (!hearingDateValue) { toast.error("Εισάγετε ημερομηνία δικασίμου"); return; }
    try {
      await axios.put(`${API}/cases/${caseId}`, { hearing_date: hearingDateValue });
      toast.success("Η δικάσιμος ενημερώθηκε");
      setEditingHearing(null);
      setHearingDateValue("");
      fetchCases(currentPage);
    } catch { toast.error("Σφάλμα ενημέρωσης"); }
  };

  const handleBulkSubject = async () => {
    if (!bulkSubjectValue || selectedIds.length === 0) return;
    try {
      await axios.post(`${API}/cases/bulk-update-subject`, { case_ids: selectedIds, subject: bulkSubjectValue });
      toast.success(`Ενημερώθηκαν ${selectedIds.length} υποθέσεις`);
      setSelectedIds([]);
      setShowBulkSubject(false);
      setBulkSubjectValue("");
      fetchCases(currentPage);
    } catch { toast.error("Σφάλμα ενημέρωσης"); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === cases.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(cases.map(c => c.id));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try { return new Date(dateStr).toLocaleDateString("el-GR"); }
    catch { return dateStr; }
  };

  const getNextDeadline = (deadlines) => {
    if (!deadlines || deadlines.length === 0) return null;
    const pending = deadlines.filter(d => d.status !== "completed").sort((a, b) => new Date(a.deadline_date) - new Date(b.deadline_date));
    return pending[0] || null;
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Generate visible page numbers
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 7;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push("...");
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  const startItem = totalCount === 0 ? 0 : ((currentPage - 1) * ITEMS_PER_PAGE) + 1;
  const endItem = Math.min(currentPage * ITEMS_PER_PAGE, totalCount);

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="cases-title">Υποθέσεις</h1>
          <p className="text-slate-500 mt-1">
            {totalCount > 0 ? `${totalCount.toLocaleString("el-GR")} υποθέσεις` : "Διαχείριση και αναζήτηση υποθέσεων"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && selectedIds.length > 0 && (
            <>
              <Button variant="outline" onClick={() => setShowBulkSubject(true)} data-testid="bulk-subject-btn">
                <FileText className="w-4 h-4 mr-2" />
                Αντικείμενο ({selectedIds.length})
              </Button>
              <Button variant="destructive" onClick={() => setShowBulkDelete(true)} data-testid="bulk-delete-btn">
                <Trash2 className="w-4 h-4 mr-2" />
                Διαγραφή ({selectedIds.length})
              </Button>
            </>
          )}
          <Button onClick={() => setShowNewCase(true)} data-testid="new-case-btn">
            <Plus className="w-4 h-4 mr-2" />Νέα Υπόθεση
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Αναζήτηση με ΓΑΚ, ΕΑΚ, αντικείμενο ή ακριβές ονοματεπώνυμο διαδίκου..." value={search}
                  onChange={e => setSearch(e.target.value)} className="pl-10 pr-12" data-testid="search-input" />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  title="Σάρωση QR Code"
                  onClick={() => setShowQrScanner(true)}
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                  data-testid="search-qr-btn"
                >
                  <ScanLine className="w-4 h-4" />
                </Button>
              </div>
              <BranchFilterSelect value={branchFilter} onChange={setBranchFilter} className="w-full md:w-60" testId="branch-filter" />
              <Select value={courtFilter} onValueChange={v => setCourtFilter(v)}
                disabled={user?.role !== "admin" && (user?.court_types || []).length === 1}>
                <SelectTrigger className="w-full md:w-48" data-testid="court-filter"><SelectValue placeholder="Τύπος Δικαστηρίου" /></SelectTrigger>
                <SelectContent>
                  {(user?.role === "admin" || (user?.court_types || []).length > 1) && (
                    <SelectItem value="all">Όλοι οι τύποι</SelectItem>
                  )}
                  {(user?.role === "admin" ? ["Μονομελές", "Πολυμελές"] : (user?.court_types || ["Μονομελές", "Πολυμελές"])).map(ct => (
                    <SelectItem key={ct} value={ct}>{ct}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={subjectFilter} onValueChange={v => setSubjectFilter(v)}>
                <SelectTrigger className="w-full md:w-56" data-testid="subject-filter"><SelectValue placeholder="Αντικείμενο" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Όλα</SelectItem>
                  {subjects.map(s => <SelectItem key={s} value={s}>{s.length > 40 ? s.substring(0,40) + "..." : s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={caseTypeFilter} onValueChange={v => setCaseTypeFilter(v)}>
                <SelectTrigger className="w-full md:w-52" data-testid="case-type-filter">
                  <SelectValue placeholder="Είδος Δικογραφίας" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">Όλα τα είδη</SelectItem>
                  {caseTypes.map(ct => (
                    <SelectItem key={ct.case_type} value={ct.case_type}>
                      {ct.case_type} ({ct.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={hearingFilter} onValueChange={v => setHearingFilter(v)}>
                <SelectTrigger className="w-full md:w-48" data-testid="hearing-filter"><SelectValue placeholder="Δικάσιμος" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Όλες</SelectItem>
                  <SelectItem value="with">Με δικάσιμο</SelectItem>
                  <SelectItem value="without">Χωρίς δικάσιμο</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col md:flex-row items-center gap-2">
              <span className="text-sm text-slate-500 whitespace-nowrap">Ημ. Κατάθεσης:</span>
              <DateInput value={filingDateFrom} onChange={e => setFilingDateFrom(e.target.value)}
                className="w-full md:w-44" placeholder="Από" data-testid="filing-date-from" />
              <span className="text-sm text-slate-400">έως</span>
              <DateInput value={filingDateTo} onChange={e => setFilingDateTo(e.target.value)}
                className="w-full md:w-44" placeholder="Έως" data-testid="filing-date-to" />
              {(filingDateFrom || filingDateTo) && (
                <Button variant="ghost" size="sm" onClick={() => { setFilingDateFrom(""); setFilingDateTo(""); }}
                  className="text-slate-400 hover:text-slate-600" data-testid="clear-date-filter">
                  <XIcon className="w-4 h-4" />
                </Button>
              )}
              <Select value={statusFilter} onValueChange={v => setStatusFilter(v)}>
                <SelectTrigger className="w-full md:w-40" data-testid="status-filter"><SelectValue placeholder="Κατάσταση" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Όλες</SelectItem>
                  <SelectItem value="active">Ενεργές</SelectItem>
                  <SelectItem value="closed">Κλειστές</SelectItem>
                  <SelectItem value="archived">Αρχειοθετημένες</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Επιπλέον φίλτρα: Δικαστής / Πινάκιο */}
            <div className="flex flex-col md:flex-row items-center gap-2 pt-2 border-t border-slate-100">
              <span className="text-sm text-slate-500 whitespace-nowrap">Δικαστής:</span>
              <Input value={judgeFilter} onChange={e => setJudgeFilter(e.target.value)}
                placeholder="Επώνυμο ή τμήμα ονόματος…"
                className="w-full md:w-72" data-testid="judge-filter-input" />
              <span className="text-sm text-slate-500 whitespace-nowrap md:ml-4">Πινάκιο:</span>
              <Input value={pinakioFilter} onChange={e => setPinakioFilter(e.target.value)}
                placeholder="π.χ. 502 ή ΚΑΛ"
                className="w-full md:w-48" data-testid="pinakio-filter-input" />
              {(judgeFilter || pinakioFilter) && (
                <Button variant="ghost" size="sm" onClick={() => { setJudgeFilter(""); setPinakioFilter(""); }}
                  className="text-slate-400 hover:text-slate-600" data-testid="clear-judge-pinakio-filter">
                  <XIcon className="w-4 h-4 mr-1" /> Καθαρισμός
                </Button>
              )}
            </div>
            {/* Δικονομικά κριτήρια Ναι/Όχι */}
            <div className="flex flex-col md:flex-row md:flex-wrap items-center gap-2 pt-2 border-t border-slate-100" data-testid="flag-filters">
              {FLAG_FILTERS.map(f => (
                <Select key={f.key} value={flagFilters[f.key]} onValueChange={v => setFlagFilters({ ...flagFilters, [f.key]: v })}>
                  <SelectTrigger className="w-full md:w-72" data-testid={`${f.testid}-filter`}><SelectValue placeholder={f.label} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{f.label}: Όλες</SelectItem>
                    <SelectItem value="yes">{f.label}: Ναι</SelectItem>
                    <SelectItem value="no">{f.label}: Όχι</SelectItem>
                  </SelectContent>
                </Select>
              ))}
              {Object.values(flagFilters).some(v => v !== "all") && (
                <Button variant="ghost" size="sm" onClick={() => setFlagFilters(EMPTY_FLAGS)}
                  className="text-slate-400 hover:text-slate-600" data-testid="clear-flag-filters">
                  <XIcon className="w-4 h-4 mr-1" /> Καθαρισμός
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-16 w-full" />)}</div>
          ) : cases.length === 0 ? (
            <div className="p-12 text-center">
              <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900">Δεν βρέθηκαν υποθέσεις</h3>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    {isAdmin && (
                      <TableHead className="w-10">
                        <Checkbox checked={selectedIds.length === cases.length && cases.length > 0}
                          onCheckedChange={toggleSelectAll} data-testid="select-all-checkbox" />
                      </TableHead>
                    )}
                    <TableHead className="font-semibold">ΓΑΚ/ΕΑΚ</TableHead>
                    <TableHead className="font-semibold">Ημ. Κατάθεσης</TableHead>
                    <TableHead className="font-semibold">Τύπος</TableHead>
                    <TableHead className="font-semibold">Δικάσιμος</TableHead>
                    <TableHead className="font-semibold">Δικαστής / Ανάθεση</TableHead>
                    <TableHead className="font-semibold">Ενάγοντες</TableHead>
                    <TableHead className="font-semibold">Επόμενη Προθεσμία</TableHead>
                    <TableHead className="font-semibold text-right">Ενέργειες</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cases.map(caseItem => {
                    const nextDeadline = getNextDeadline(caseItem.deadlines);
                    const isOverdue = nextDeadline && new Date(nextDeadline.deadline_date) < new Date();
                    return (
                      <TableRow key={caseItem.id} className="case-row cursor-pointer"
                        onClick={() => navigate(`/cases/${caseItem.id}`)} data-testid={`case-row-${caseItem.id}`}>
                        {isAdmin && (
                          <TableCell onClick={e => e.stopPropagation()}>
                            <Checkbox checked={selectedIds.includes(caseItem.id)}
                              onCheckedChange={() => toggleSelect(caseItem.id)}
                              data-testid={`select-case-${caseItem.id}`} />
                          </TableCell>
                        )}
                        <TableCell>
                          <div className="font-mono">
                            <span className="font-semibold text-slate-900">ΓΑΚ {caseItem.gak_number}/{caseItem.gak_year} · ΕΑΚ {caseItem.eak_number || "—"}/{caseItem.gak_year}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-600">{formatDate(caseItem.filing_date)}</TableCell>
                        <TableCell>
                          <Badge className={caseItem.court_type === "Μονομελές" ? "court-mono" : "court-poly"}>{caseItem.court_type}</Badge>
                        </TableCell>
                        <TableCell>
                          {editingHearing === caseItem.id ? (
                            <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                              <DateInput className="h-8 w-32 text-sm" value={hearingDateValue}
                                onChange={e => setHearingDateValue(e.target.value)} data-testid={`hearing-date-input-${caseItem.id}`} />
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-600"
                                onClick={() => handleSaveHearingDate(caseItem.id)} data-testid={`save-hearing-${caseItem.id}`}>
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400"
                                onClick={() => { setEditingHearing(null); setHearingDateValue(""); }}>
                                <XIcon className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : caseItem.last_hearing_date && caseItem.last_hearing_date !== "None" ? (
                            <div className="text-sm">
                              <div className="flex items-center gap-1">
                                <CalendarCheck className="w-3.5 h-3.5 text-emerald-500" />
                                <span className="text-slate-600 truncate max-w-[160px]" title={caseItem.last_hearing_date}>
                                  {caseItem.last_hearing_date.length > 25 ? caseItem.last_hearing_date.substring(0, 25) + "..." : caseItem.last_hearing_date}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                              <Badge className="bg-red-50 text-red-700 border border-red-200 text-xs cursor-pointer hover:bg-red-100"
                                onClick={() => { setEditingHearing(caseItem.id); setHearingDateValue(""); }}
                                data-testid={`no-hearing-badge-${caseItem.id}`}>
                                <CalendarX className="w-3 h-3 mr-1" />
                                Χωρίς δικάσιμο
                              </Badge>
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="max-w-[180px] text-xs">
                          {caseItem.assigned_judge ? (
                            <div className="flex flex-col gap-0.5">
                              <span className="font-semibold text-slate-800 truncate" title={caseItem.assigned_judge}>{caseItem.assigned_judge}</span>
                              {caseItem.assigned_judge_at && (
                                <span className="text-slate-500" data-testid={`case-judge-at-${caseItem.id}`}>
                                  Ανάθεση: {formatDate(caseItem.assigned_judge_at.split("T")[0])}
                                </span>
                              )}
                              {caseItem.assignment_act_number && (
                                <span className="text-emerald-700" title="Αρ. Πράξης Ορισμού">
                                  Πράξη: {caseItem.assignment_act_number}/{caseItem.assignment_act_year || caseItem.gak_year}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </TableCell>
                        <TableCell className="max-w-[150px] truncate text-slate-600">{p(caseItem.plaintiffs) || "-"}</TableCell>
                        <TableCell>
                          {nextDeadline ? (
                            <div className={`text-sm ${isOverdue ? "text-red-600" : "text-slate-600"}`}>
                              <p className="font-medium">{nextDeadline.title}</p>
                              <p className="text-xs">{formatDate(nextDeadline.deadline_date)}</p>
                            </div>
                          ) : <span className="text-slate-400">-</span>}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2" onClick={e => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" onClick={() => navigate(`/cases/${caseItem.id}`)} data-testid={`view-case-${caseItem.id}`}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            {isAdmin && (
                              <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                onClick={() => setDeleteId(caseItem.id)} data-testid={`delete-case-${caseItem.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Server-side Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t" data-testid="pagination-controls">
                  <p className="text-sm text-slate-500" data-testid="pagination-info">
                    {startItem.toLocaleString("el-GR")} - {endItem.toLocaleString("el-GR")} από {totalCount.toLocaleString("el-GR")}
                  </p>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="sm" onClick={() => goToPage(1)} disabled={currentPage === 1} data-testid="first-page-btn">
                      <ChevronsLeft className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} data-testid="prev-page-btn">
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    {getPageNumbers().map((p, i) => (
                      p === "..." ? (
                        <span key={`dots-${i}`} className="px-2 text-slate-400">...</span>
                      ) : (
                        <Button key={p} variant={p === currentPage ? "default" : "outline"} size="sm"
                          className="min-w-[36px]" onClick={() => goToPage(p)}
                          data-testid={`page-${p}-btn`}>
                          {p}
                        </Button>
                      )
                    ))}
                    <Button variant="outline" size="sm" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} data-testid="next-page-btn">
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => goToPage(totalPages)} disabled={currentPage === totalPages} data-testid="last-page-btn">
                      <ChevronsRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Single Delete Dialog */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Διαγραφή Υπόθεσης</DialogTitle>
            <DialogDescription>Η ενέργεια δεν μπορεί να αναιρεθεί.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>Ακύρωση</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">Διαγραφή</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog open={showBulkDelete} onOpenChange={setShowBulkDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Μαζική Διαγραφή</DialogTitle>
            <DialogDescription>Θέλετε να διαγράψετε {selectedIds.length} υποθέσεις; Η ενέργεια δεν μπορεί να αναιρεθεί.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkDelete(false)}>Ακύρωση</Button>
            <Button variant="destructive" onClick={handleBulkDelete} data-testid="confirm-bulk-delete-btn">
              Διαγραφή {selectedIds.length} υποθέσεων
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Subject Dialog */}
      <Dialog open={showBulkSubject} onOpenChange={setShowBulkSubject}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Μαζική Ανάθεση Αντικειμένου</DialogTitle>
            <DialogDescription>Επιλέξτε αντικείμενο για {selectedIds.length} υποθέσεις</DialogDescription>
          </DialogHeader>
          <Select value={bulkSubjectValue} onValueChange={setBulkSubjectValue}>
            <SelectTrigger data-testid="bulk-subject-select"><SelectValue placeholder="Επιλέξτε αντικείμενο" /></SelectTrigger>
            <SelectContent>
              {subjects.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkSubject(false)}>Ακύρωση</Button>
            <Button onClick={handleBulkSubject} disabled={!bulkSubjectValue} data-testid="confirm-bulk-subject-btn">
              Ανάθεση σε {selectedIds.length} υποθέσεις
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NewCaseDialog open={showNewCase} onOpenChange={setShowNewCase} onSuccess={() => { fetchCases(currentPage); setShowNewCase(false); }} />
      <QRScannerDialog open={showQrScanner} onOpenChange={setShowQrScanner} onScan={handleQrScanned} />
    </div>
  );
};

export default CasesList;
